import { useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import type { Template } from "@/common/types/templates";
import { Layout } from "@/components/layout";
import { TemplateForm } from "@/components/ui/templates/template-form";
import { useTemplateWrites } from "@/hooks/templates/useTemplateWrites";
import { toast } from "@/hooks/use-toast";
import { refreshTemplateReads } from "@/lib/template-queries";
import { container } from "@/lib/utils";
import { getVisitScope, type VisitScope } from "@/lib/visit-queries";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";
import { TemplateDocumentEditor } from "../Templates/redesign/TemplateDocumentEditor";

export const TemplateCreate = (): JSX.Element => {
  const { t } = useTranslation();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const navigate = useNavigate();
  const { create, loading } = useTemplateWrites();
  const handleAssistantSaved = (
    _template: Template,
    scope: VisitScope | null,
  ): void => {
    if (!scope || getVisitScope() !== scope) return;
    if (!mounted.current) {
      void refreshTemplateReads(scope);
      return;
    }
    toast({
      variant: "default",
      title: t("templates.createdToast.successful.title"),
      description: t("templates.createdToast.successful.description"),
    });
    void refreshTemplateReads(scope);
    void navigate({ to: "/templates" });
  };
  // v3.2 editor: whether the last save went through (it then leaves).
  const savedRef = useRef(false);
  const handleCreateItem = async (
    data: TemplateFormSchemaType,
  ): Promise<void> => {
    savedRef.current = false;
    const outcome = await create({
      name: data.name,
      headers: data.headers,
      optionalText: data.optionalText ?? "",
      isDefault: data.isDefault,
    });
    if (!outcome || getVisitScope() !== outcome.scope) return;
    if (!mounted.current) {
      if (outcome.ok) void refreshTemplateReads(outcome.scope);
      return;
    }
    toast({
      variant: outcome.ok ? "default" : "error",
      title: t(
        outcome.ok
          ? "templates.createdToast.successful.title"
          : "templates.createdToast.error.title",
      ),
      description: t(
        outcome.ok
          ? "templates.createdToast.successful.description"
          : "templates.createdToast.error.description",
      ),
    });
    if (outcome.ok) {
      savedRef.current = true;
      void refreshTemplateReads(outcome.scope);
      void navigate({ to: "/templates" });
    }
  };

  const form = (
    <TemplateForm
      isSubmitting={loading}
      assistant={{ onSaved: handleAssistantSaved }}
      submitText={t("templates.createTemplateForm.createTemplateButton")}
      onCancel={() => navigate({ to: "/templates" })}
      onSubmit={handleCreateItem}
    />
  );
  const layout = {
    navbarTitle: t("templates.title"),
    breadcrumbs: [
      { name: t("templates.title"), href: `/templates`, current: true },
    ],
  };
  // v3.2: the "Document" editor (section cards, Ask bar), full column.
  if (AppConfig.enableAppRedesign)
    return (
      <Layout navbarTitle={layout.navbarTitle} topBar="none">
        <div className="absolute inset-0">
          <TemplateDocumentEditor
            saving={loading}
            onClose={() => {
              void navigate({ to: "/templates" });
            }}
            onSave={async (write) => {
              await handleCreateItem(write);
              return savedRef.current;
            }}
          />
        </div>
      </Layout>
    );

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="min-h-screen supports-[min-height:100dvh]:min-h-dvh bg-background p-6">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {form}
        </motion.div>
      </div>
    </Layout>
  );
};
